/**
 * Yotpo API — Centralised fetch layer
 * ────────────────────────────────────
 * Every Yotpo call in the theme goes through this file.
 * Sections / snippets load it via <script src="{{ 'yotpo-api.js' | asset_url }}" defer>
 * and then call, for example:
 *
 *   const data = await YotpoAPI.reviews.forProduct(productId, { page: 1 });
 *   const bl   = await YotpoAPI.reviews.bottomline(productId);
 *   const imgs = await YotpoAPI.ugc.forProduct(productId, { page: 1 });
 *
 * The App Key is set once via YotpoAPI.configure({ appKey, middlewareUrl }).
 * Liquid should call configure() before any fetches.
 */

window.YotpoAPI = (() => {
  /* ─── Config ─── */
  const BASE = 'https://api.yotpo.com';
  let _appKey = '';
  let _middlewareUrl = '';

  /**
   * Call once on page load (usually from theme.liquid or the first section that needs it).
   * @param {{ appKey: string, middlewareUrl?: string }} opts
   */
  function configure({ appKey, middlewareUrl } = {}) {
    if (appKey) _appKey = appKey;
    if (middlewareUrl) _middlewareUrl = middlewareUrl;
  }

  /* ─── Helpers ─── */

  /** Yotpo CDN URLs often lack a protocol*/
  function ensureProtocol(url) {
    if (!url) return '';
    if (url.startsWith('//')) return 'https:' + url;
    return url;
  }

  /** Generic JSON fetcher with error logging. */
  async function _get(url) {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Yotpo API ${res.status}: ${url}`);
    return res.json();
  }

  const reviews = {
    /**
     * Fetch reviews for a single product
     * @param {string|number} productId  — Shopify product ID
     * @param {Object}  opts
     * @param {number}  [opts.page=1]
     * @param {number}  [opts.perPage=5]
     * @param {number}  [opts.star]       — 1-5, filter by rating
     * @param {string}  [opts.sort=date]  — date | rating | votes_up
     * @param {string}  [opts.direction=desc] — asc | desc
     * @returns {Promise<Object>} — { reviews, bottomline, pagination }
     */
    async forProduct(productId, opts = {}) {
      const { page = 1, perPage = 5, star, sort, direction } = opts;
      let url = `${BASE}/v1/widget/${_appKey}/products/${productId}/reviews.json?page=${page}&per_page=${perPage}`;
      if (star) url += `&star=${star}`;
      if (sort) url += `&sort=${sort}`;
      if (direction) url += `&direction=${direction}`;

      const json = await _get(url);
      return json.response;
    },

    /**
     * Fetch bottomline for a product
     * @param {string|number} productId
     * @returns {Promise<{ total_review: number, average_score: number, star_distribution: Object }>}
     */
    async bottomline(productId) {
      const json = await _get(
        `${BASE}/products/${_appKey}/${productId}/bottomline`
      );
      return json.response?.bottomline || json.response || json;
    },

    /**
     * Fetch site wide reviews
     */
    async siteWide(opts = {}) {
      return this.forProduct('yotpononproductrelatedwidget', opts);
    },

    /**
     * Fetch all reviews via merchant endpoint (authenticated needs pipedream middleware)
     * @param {Object} opts
     * @param {number} [opts.page=1]
     * @param {number} [opts.count=10]
     * @returns {Promise<Object>}
     */
    async all(opts = {}) {
      if (!_middlewareUrl) throw new Error('YotpoAPI: middlewareUrl not configured (needed for authenticated calls)');
      const { page = 1, count = 10 } = opts;
      const url = `${_middlewareUrl}?action=reviews&page=${page}&count=${count}`;
      return _get(url);
    },
  };


  const ugc = {
    /**
     * Fetch UGC images tagged to a specific product
     * @param {string|number} productId
     * @param {Object} opts
     * @param {number} [opts.page=1]
     * @param {number} [opts.perPage=20]
     * @returns {Promise<{ images: Array, pagination: Object }>}
     */
    async forProduct(productId, opts = {}) {
      const { page = 1, perPage = 20 } = opts;
      const url = `${BASE}/v1/widget/${_appKey}/albums/product/${productId}?per_page=${perPage}&page=${page}`;
      const json = await _get(url);
      return _parseUGCResponse(json);
    },

    /**
     * Fetch UGC images from a custom album by name
     * @param {string} albumName
     * @param {Object} opts
     * @param {number} [opts.page=1]
     * @param {number} [opts.perPage=20]
     * @returns {Promise<{ images: Array, pagination: Object }>}
     */
    async forAlbum(albumName, opts = {}) {
      const { page = 1, perPage = 20 } = opts;
      const url = `${BASE}/v1/widget/${_appKey}/albums/by_name?album_name=${encodeURIComponent(albumName)}&per_page=${perPage}&page=${page}`;
      const json = await _get(url);
      return _parseUGCResponse(json);
    },

    /**
     * Export all UGC images (authenticated needs pipedream middleware)
     * @param {Object} opts
     * @param {string} [opts.source=review]
     * @param {number} [opts.page=1]
     * @param {number} [opts.perPage=10]
     * @param {string} [opts.mediaType]
     * @returns {Promise<Object>}
     */
    async exportAll(opts = {}) {
      if (!_middlewareUrl) throw new Error('YotpoAPI: middlewareUrl not configured (needed for authenticated calls)');
      const { source = 'review', page = 1, perPage = 10, mediaType } = opts;
      let url = `${_middlewareUrl}?action=ugc_export&source=${source}&page=${page}&per_page=${perPage}`;
      if (mediaType) url += `&media_type=${mediaType}`;
      return _get(url);
    },
  };

  function _parseUGCResponse(json) {
    if (json.status?.code !== 200) {
      throw new Error(json.status?.message || 'Yotpo UGC error');
    }

    const pagination = json.response?.pagination || {};
    const rawImages = json.response?.images || [];

    const images = rawImages.map((img) => {
      const isVideo = img.media_type === 'video';
      return {
        type: isVideo ? 'video' : 'image',
        url: ensureProtocol(img.original_image_url || img.image_url),
        thumb: ensureProtocol(
          img.image_url || img.original_image_url || img.thumbnail_url
        ),
        medium: ensureProtocol(img.image_url || img.original_image_url),
        original: ensureProtocol(img.original_image_url || img.image_url),
        videoUrl: isVideo ? ensureProtocol(img.standard_resolution_url) : null,
        username: img.post?.username || img.post?.user_name || '',
        caption: img.post?.content || '',
        taggedProducts: (img.tagged_products || []).map((p) => ({
          id: p.domain_key || p.product_id,
          name: p.name,
          url: p.product_url || p.product_link,
          imageUrl: ensureProtocol(p.image_url),
          score: p.score,
          reviewsCount: p.reviews_count,
        })),
      };
    });

    return { images, pagination };
  }


  return {
    configure,
    ensureProtocol,
    reviews,
    ugc,
  };
})();

